import { Button, ButtonSize, ButtonTheme } from './Button';
import { RadioButton, RadioGroup } from './RadioGroup';
import { Article, Price, SubscriptionPeriodType } from '../../../models/Article';
import { SubscriptionPeriodPricesOut } from '../../api/subscriptions/fetchSubscriptionStartOptions';
import { ProductMetaData } from '../../../models/Product';
import ModalDialog from '../components/modal/ModalDialog';
import { AvailablePricesSelector } from './AvailablePricesSelector/AvailablePricesSelector';
import React, { FC, Fragment, useState } from 'react';
import styled from 'styled-components';
import { THEME_WARNING, themeColor } from '../../styles/basics/colors';
import MaterialIcon from './MaterialIcon';
import LoadingSpinner from '../components/LoadingSpinner';
import { useTranslation } from 'react-i18next';
import { useTarget } from './useTarget';

type Props = {
    product: ProductMetaData & Article;
    showPopup: boolean;
    hidePopup: () => void;
    selectedPeriod: SubscriptionPeriodType;
    setSelectedPeriod: (period: SubscriptionPeriodType) => void;
    selectedPrice?: Price;
    setSelectedPrice: (price: Price) => void;
    subscriptionPeriodPrices?: SubscriptionPeriodPricesOut[];
    onConfirm: () => Promise<void>;
    activeSubscriptions: number;
    futureSubscriptions: number;
    isLoading: boolean;
};

export const SubscriptionPeriodPopup: FC<Props> = ({
    product,
    showPopup,
    hidePopup,
    selectedPeriod,
    setSelectedPeriod,
    selectedPrice,
    setSelectedPrice,
    subscriptionPeriodPrices,
    onConfirm,
    activeSubscriptions,
    futureSubscriptions,
    isLoading
}) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const { target } = useTarget();

    return (
        <>
            <ModalDialog
                title={t('components.subscriptionPeriodPopup.title')}
                show={showPopup}
                boldHeader={false}
                titleClassName="popup-title"
                onCloseButtonClick={!loading ? hidePopup : undefined}
            >
                <StContent id="subscription-start-popup">
                    <StSubtitle>{t('components.subscriptionPeriodPopup.subtitle')}</StSubtitle>
                    {isLoading ? (
                        <StLoadingContainer>
                            <LoadingSpinner width={30} />
                        </StLoadingContainer>
                    ) : (
                        <StSubscriptionForm>
                            <RadioGroup
                                isInline={false}
                                value={selectedPeriod}
                                onChange={newPeriod => setSelectedPeriod(newPeriod as SubscriptionPeriodType)}
                                className="add-to-selector-buttons"
                            >
                                {subscriptionPeriodPrices?.map(({ subscriptionPeriod, availablePrices }) => {
                                    return (
                                        <Fragment key={subscriptionPeriod.type}>
                                            <RadioButton value={subscriptionPeriod.type}>
                                                <StRadioButtonLabel>
                                                    {subscriptionPeriod.type === SubscriptionPeriodType.CURRENT
                                                        ? t('components.subscriptionPeriodPopup.immediately')
                                                        : t('components.subscriptionPeriodPopup.nextSubscriptionYear')}
                                                </StRadioButtonLabel>
                                                <StRadioButtonDateLabel>
                                                    {' '}
                                                    {t('components.subscriptionPeriodPopup.startAndEndDate', {
                                                        startDate: t('dateFormat.dateWithMonth', {
                                                            val: new Date(subscriptionPeriod.startDate)
                                                        }),
                                                        endDate: t('dateFormat.dateWithMonth', {
                                                            val: new Date(subscriptionPeriod.endDate)
                                                        }),
                                                        count: subscriptionPeriod.lengthInDays
                                                    })}
                                                </StRadioButtonDateLabel>
                                            </RadioButton>
                                            {subscriptionPeriod.type === 'CURRENT' && activeSubscriptions > 0 ? (
                                                <StWarningMessage>
                                                    <MaterialIcon icon="warning" />
                                                    {t('components.subscriptionPeriodPopup.currentCollectionWarning', {
                                                        count: activeSubscriptions
                                                    })}
                                                </StWarningMessage>
                                            ) : null}
                                            {subscriptionPeriod.type === 'NEXT' && futureSubscriptions > 0 ? (
                                                <StWarningMessage>
                                                    <MaterialIcon icon="warning" />
                                                    {t(
                                                        'components.subscriptionPeriodPopup.nextYearsCollectionWarning',
                                                        {
                                                            count: futureSubscriptions
                                                        }
                                                    )}
                                                </StWarningMessage>
                                            ) : null}
                                            {product ? (
                                                <StPriceSelectorContainer>
                                                    <AvailablePricesSelector
                                                        product={{
                                                            ...product,
                                                            price: selectedPrice,
                                                            availablePrices: availablePrices as Price[]
                                                        }}
                                                        customTermLimitForLabel={subscriptionPeriod.lengthInDays}
                                                        target={target}
                                                        onSelectPrice={nextPrice => {
                                                            setSelectedPeriod(subscriptionPeriod.type);
                                                            setSelectedPrice(nextPrice);
                                                        }}
                                                        disabled={true}
                                                        showSelectedPrice={true}
                                                    />
                                                </StPriceSelectorContainer>
                                            ) : null}
                                        </Fragment>
                                    );
                                })}
                            </RadioGroup>
                        </StSubscriptionForm>
                    )}

                    <StFooter>
                        <Button
                            theme={ButtonTheme.POSITIVE_INVERT}
                            onClick={() => hidePopup()}
                            text={t('cancel')}
                            size={ButtonSize.LARGE}
                            disabled={loading}
                        />

                        <Button
                            onClick={() => {
                                setLoading(true);
                                onConfirm().finally(() => setLoading(false));
                            }}
                            text={t('confirm')}
                            size={ButtonSize.LARGE}
                            className="ml-sm"
                            disabled={loading}
                            isLoading={loading}
                        />
                    </StFooter>
                </StContent>
            </ModalDialog>
        </>
    );
};

export const StContent = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    flex-direction: column;
    min-height: 200px;
`;

export const StFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 20px;
    width: 100%;
`;

export const StSubtitle = styled.p`
    font-size: 14px;
    line-height: 17px;
    margin-bottom: 26px;
`;

const StLoadingContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    width: 100%;
`;

const StSubscriptionForm = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: center;
    margin-bottom: 20px;
    flex-direction: column;
    font-size: 14px;
    font-weight: bold;
`;

const StRadioButtonLabel = styled.span`
    margin-left: 10px;
`;
const StRadioButtonDateLabel = styled.span`
    color: #979797;
`;

const StPriceSelectorContainer = styled.div`
    margin: 8px 0;
    width: 35%;
    padding-left: 23px;
`;

const StWarningMessage = styled.p`
    display: flex;
    align-items: center;
    justify-content: flex-start;

    padding: 8px;
    margin: 4px 0 12px 23px;

    border-radius: 2px;
    border: 1px solid ${themeColor(THEME_WARNING)};

    font-size: 12px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;

    & i {
        color: ${themeColor(THEME_WARNING)};
        margin-right: 5px;
    }
`;
